/*
 Copyright 2019-present wobe-systems GmbH

 Licensed under the Apache License, Version 2.0 (the 'License');
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

     http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an 'AS IS' BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
*/
import { ActionTree } from 'vuex';
import * as types from '@/store/types';
import axios from 'axios';
import crypto from 'crypto';
import { handleErrorMessage, handleInfoMessage } from '@/store/handleCatchError';
import { OK, SERVICES } from '@/middlewares/httpClient';
import {
  User, LoginUser, UserState, FlowOwner,
} from './models';
import { RootState } from '../models';
import {
  signupUser,
  loginUser,
  logoutUser,
  fetchLoggedInUser,
  updateUser,
  changePassword,
  deleteUser,
  fetchUsers,
  setUploadRight,
  setAdminRight,
  unsetUploadRight,
  unsetAdminRight,
  fetchUser,
  requestResetPassword,
  resetPassword,
  signupRequest,
  sendMessageToAllUsers,
} from './api';

const service: string = SERVICES.get('userservice');

export const actions: ActionTree<UserState, RootState> = {

  // SIGNUP
  async [types.SIGNUP](
    { commit, dispatch },
    { user, token }: { user: User; token: string },
  ): Promise<any> {
    await signupUser(user, token)
      .then(({ status }) => {
        const result: LoginUser = { success: status === OK, user };
        if (status === OK) {
          commit(types.MUTATE_USER_LOGIN, result);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },

  // LOGIN
  async [types.LOGIN]({ commit, dispatch }, user: User): Promise<any> {
    await loginUser(user)
      .then(({ status, data }) => {
        const result: LoginUser = { success: status === OK, user: data as User };
        if (status === OK) {
          commit(types.MUTATE_USER_LOGIN, result);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },

  // LOGOUT
  async [types.LOGOUT]({ commit, dispatch }, user: User): Promise<any> {
    await logoutUser()
      .then(({ status }) => {
        const result: boolean = status === OK;
        if (status === OK) {
          commit(types.MUTATE_USER_LOGOUT, result);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },

  // FETCH_LOGGED_IN_USER
  async [types.FETCH_LOGGED_IN_USER]({ commit, dispatch }): Promise<any> {
    await fetchLoggedInUser()
      .then(({ status, data }) => {
        const user: User = data;

        if (status === OK) {
          commit(types.MUTATE_USER, user);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },

  // FETCH_USER
  async [types.FETCH_USER]({ commit, state }, { id }: { id: string }): Promise<any> {
    // Check if user already is in store
    const user = state.flowOwners.find((flowOwner) => flowOwner.id === id);

    return new Promise((resolve, reject) => {
      // Fetch only if user is not in store
      if (!user) {
        fetchUser(id)
          .then(({ status, data }) => {
            if (status === OK) {
              resolve(data);
            }
          }).catch((err) => reject(err));
      } else {
        resolve(user);
      }
    });
  },

  // UPDATE_USER
  async [types.UPDATE_USER]({ dispatch },
    { user, currentEmail }: {user: User, currentEmail: string}) {
    return new Promise((resolve, reject) => {
      updateUser(user)
        .then(async ({ status }) => {
          if (status === OK) {
            const cu = user;
            cu.email = currentEmail;
            await dispatch(types.LOGIN, cu);
            handleInfoMessage('updated_successfully', dispatch);
            resolve(status);
          }
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject();
        });
    });
  },

  // CHANGE_PASSWORD
  async [types.CHANGE_PASSWORD](
    { dispatch },
    { oldPassword, user }: { oldPassword: string; user: User },
  ) {
    return new Promise((resolve, reject) => {
      changePassword(oldPassword, user)
        .then(async ({ status }) => {
          if (status === OK) {
            await dispatch(types.LOGIN, user);
            handleInfoMessage('updated_successfully', dispatch);
            resolve(status);
          }
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject();
        });
    });
  },

  // REQUEST RESET PASSWORD
  async [types.REQUEST_RESET_PASSWORD](
    { commit, dispatch },
    { email }: {email: string },
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      requestResetPassword(email)
        .then(async (result: any) => {
          const { status } = result;
          if (status === OK) {
            commit(types.MUTATE_FORGET_PASSWORD_REQUEST, true);
            resolve(status);
          }
        })
        .catch((err: any) => {
          commit(types.MUTATE_FORGET_PASSWORD_REQUEST, false);
          handleErrorMessage(err, dispatch, service);
          reject();
        });
    });
  },

  // RESET PASSWORD
  async [types.RESET_PASSWORD](
    { commit, dispatch },
    { token, password }: {token: string, password: string },
  ):
    Promise<any> {
    return new Promise((resolve, reject) => {
      resetPassword(token, password)
        .then(async (result: any) => {
          const { status } = result;
          if (status === OK) {
            handleInfoMessage('updated_successfully', dispatch);
            resolve(status);
          }
        })
        .catch((err: any) => {
          handleErrorMessage(err, dispatch, service);
          // to redirect to login page
          resolve(true);
        });
    });
  },

  // DELETE_USER
  async [types.DELETE_USER]({ commit, dispatch }, user: User) {
    return new Promise((resolve, reject) => {
      deleteUser(user)
        .then(async (result) => {
          const { status } = result;
          if (status === OK) {
            commit(types.MUTATE_USER_LOGOUT, result);
            resolve(true);
          }
        })
        .catch((err) => {
          handleErrorMessage(err, dispatch, service);
          reject();
        });
    });
  },

  // FETCH_USERS
  async [types.FETCH_USERS]({ commit, dispatch }): Promise<any> {
    await fetchUsers()
      .then(({ status, data }) => {
        const users: User[] = data;

        if (status === OK) {
          commit(types.MUTATE_USERS, users);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },

  // Make user admin
  async [types.SET_ADMIN_RIGHT]({ commit, dispatch }, id: string): Promise<any> {
    await setAdminRight(id)
      .then(({ status, data }) => {
        if (status === OK) {
          const user: any = { id, isAdmin: true };
          commit(types.MUTATE_USERS_ADMIN_RIGHT, user);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },
  async [types.UNSET_ADMIN_RIGHT]({ commit, dispatch }, id: string): Promise<any> {
    await unsetAdminRight(id)
      .then(({ status }) => {
        if (status === OK) {
          const user: any = { id, isAdmin: false };
          commit(types.MUTATE_USERS_ADMIN_RIGHT, user);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },
  // Make user admin
  async [types.SET_UPLOAD_RIGHT]({ commit, dispatch }, id: string): Promise<any> {
    await setUploadRight(id)
      .then(({ status }) => {
        if (status === OK) {
          const user: any = { id, isAllowedToUpload: true };
          commit(types.MUTATE_USERS_UPLOAD_RIGHT, user);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },
  // ALLOW TO UPLOAD BRICK PACKAGES
  async [types.UNSET_UPLOAD_RIGHT]({ commit, dispatch }, id: string): Promise<any> {
    await unsetUploadRight(id)
      .then(({ status }) => {
        if (status === OK) {
          const user: any = { id, isAllowedToUpload: false };
          commit(types.MUTATE_USERS_UPLOAD_RIGHT, user);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },

  async [types.SIGNUP_REQUEST]({ commit, dispatch }, email: string): Promise<any> {
    await signupRequest(email)
      .then(({ status }) => {
        if (status === OK) {
          commit(types.MUTATE_SIGNUP_REQUEST, true);
        }
      })
      .catch((err) => {
        commit(types.MUTATE_SIGNUP_REQUEST, false);
        handleErrorMessage(err, dispatch, service);
      });
  },

  [types.RESET_SIGNUP_REQUEST]({ commit }) {
    commit(types.MUTATE_SIGNUP_REQUEST, false);
  },

  async [types.FETCH_GRAVATAR]({ commit }, email: string): Promise<any> {
    const hashEmail = crypto
      .createHash('md5')
      .update(email)
      .digest('hex');
    const url = `https://www.gravatar.com/avatar/${hashEmail}?d=404`;
    return new Promise((resolve, reject) => {
      axios
        .get(url)
        .then(() => {
          commit(types.MUTATE_GRAVATARS, { email, url });
          resolve(url);
        })
        .catch(() => {
          commit(types.MUTATE_GRAVATARS, { email, url: '' });
          reject();
        });
    });
  },

  async [types.SEND_MESSAGE_TO_ALL_USERS]({ commit, dispatch }, data: any): Promise<any> {
    await sendMessageToAllUsers(data.id, data.subject, data.message)
      .then(({ status }) => {
        if (status === OK) {
          handleInfoMessage('message_sent', dispatch);
        }
      })
      .catch((err) => {
        handleErrorMessage(err, dispatch, service);
      });
  },

  [types.REMOVE_USER_STATE]({ commit, dispatch }) {
    commit(types.MUTATE_REMOVE_USER_STATE);
  },
};
